import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { getCurrentUser, isViewOnlyRole, isAllBranchesRole } from '../utils/auth';
import { useToast } from './ToastContext';
import { useAutoSync } from '../hooks/useAutoSync';

const ScrollHintIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

function ReportView({ companyId }) {
  const user = getCurrentUser();
  const canEdit = !isViewOnlyRole(user?.role);
  const canSelectAllBranches = isAllBranchesRole(user?.role);
  const [arData, setArData] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [expandedSp, setExpandedSp] = useState(new Set());
  
  // Toast notifications
  const { showToast } = useToast();
  
  // Auto-sync monitoring
  useAutoSync(true);

  // Enhanced features: search, pagination, date filter, inline editing
  const [salespersonFilter, setSalespersonFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(1000);

  // Branch filter
  const ALL_BRANCHES_VALUE = '';
  const [selectedBranch, setSelectedBranch] = useState(companyId ? String(companyId) : '');
  const [companies, setCompanies] = useState([]);

  // Track customer data for modal editing
  const [customerData, setCustomerData] = useState({});

  // Modal state for editing
  const [editModal, setEditModal] = useState({
    open: false,
    customerId: null,
    customerName: '',
    total_collectibles: 0,
    amount_collected: 0,
    pdc: 0,
    discount_amount: 0,
    other_deductions: 0,
    balance_collectible: 0,
    remarks: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  // Cache status
  const [cacheStatus, setCacheStatus] = useState({ last_sync_at: null, total_records: 0 });
  const [syncingCache, setSyncingCache] = useState(false);

  useEffect(() => {
    fetchReports();
    fetchCacheStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const getCompanyIdParam = () => {
    if (!canSelectAllBranches && !selectedBranch) return companyId || '';
    return selectedBranch === ALL_BRANCHES_VALUE ? '' : selectedBranch;
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const companyIdParam = getCompanyIdParam();
      const params = new URLSearchParams();
      if (selectedDate) params.append('date', selectedDate);
      if (salespersonFilter) params.append('salesperson_name', salespersonFilter);
      if (companyIdParam) params.append('company_id', companyIdParam);

      const response = await api.get(`/reports/ar-aging?${params.toString()}`);
      setArData(response.data);

      // Extract unique salespersons
      const spMap = new Map();
      response.data.forEach(row => {
        const spId = row.salesperson_id || 0;
        if (!spMap.has(spId)) {
          spMap.set(spId, {
            id: spId,
            code: row.salesperson_code || 'NO SALESPERSON',
            name: row.salesperson_name || (row.salesperson_code ? `Salesperson ${row.salesperson_code}` : 'Unassigned')
          });
        }
        // Store customer data for inline editing
        setCustomerData(prev => ({
          ...prev,
          [row.customer_id]: {
            amount_collected: row.amount_collected !== undefined ? row.amount_collected : (prev[row.customer_id]?.amount_collected || 0),
            pdc: row.pdc !== undefined ? row.pdc : (prev[row.customer_id]?.pdc || 0),
            discount_amount: row.discount_amount !== undefined ? row.discount_amount : (prev[row.customer_id]?.discount_amount || 0),
            other_deductions: row.other_deductions !== undefined ? row.other_deductions : (prev[row.customer_id]?.other_deductions || 0),
            balance_collectibles: row.balance_collectibles !== undefined ? row.balance_collectibles : (prev[row.customer_id]?.balance_collectibles || 0),
            remarks: row.remarks || prev[row.customer_id]?.remarks || '',
            notes: row.notes || prev[row.customer_id]?.notes || ''
          }
        }));
      });
      setSalespersons(Array.from(spMap.values()).sort((a, b) => a.name.localeCompare(b.name)));

      // Auto-expand all salespersons
      setExpandedSp(new Set(Array.from(spMap.keys())));
    } catch (err) {
      setMessage('Failed to load AR aging report.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCacheStatus = async () => {
    try {
      const response = await api.get('/reports/ar-aging-cache-status');
      setCacheStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch cache status:', err);
    }
  };

  const refreshCache = async () => {
    setSyncingCache(true);
    setMessage('Refreshing cache from source data...');
    try {
      // Pass the selected date to sync with ERP corrections
      const response = await api.post('/reports/cache-sync?date=' + selectedDate);
      const recordsCount = response.data.records_cached || 0;
      
      // Show toast notification
      showToast(`✅ Cache refreshed successfully! ${recordsCount} records updated.`, 'success', 5000);
      
      setMessage(`Cache refreshed successfully! ${recordsCount} records cached.`);
      await fetchCacheStatus();
      await fetchReports();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to refresh cache.';
      showToast(`❌ ${errorMsg}`, 'error', 8000);
      setMessage('Failed to refresh cache.');
    } finally {
      setSyncingCache(false);
    }
  };

  // Filter data based on separate salesperson and customer filters (date filtering is now server-side)
  const getFilteredData = () => {
    return arData.filter(row => {
      // Salesperson filter: only show customers for matching salesperson
      const matchesSalesperson = !salespersonFilter ||
        row.salesperson_name.toLowerCase().includes(salespersonFilter.toLowerCase());
      
      // Customer filter: match against customer name, code, or no
      const matchesCustomer = !customerFilter ||
        row.customer_name.toLowerCase().includes(customerFilter.toLowerCase()) ||
        (row.customer_no && row.customer_no.toLowerCase().includes(customerFilter.toLowerCase())) ||
        (row.customer_code && row.customer_code.toLowerCase().includes(customerFilter.toLowerCase()));
      
      return matchesSalesperson && matchesCustomer;
    });
  };

  const filteredData = getFilteredData();

  // Pagination
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Group ALL filtered data by salesperson (for totals calculation)
  const allGroupedBySp = {};
  filteredData.forEach(row => {
    const spId = row.salesperson_id || 0;
    if (!allGroupedBySp[spId]) {
      allGroupedBySp[spId] = [];
    }
    allGroupedBySp[spId].push(row);
  });

  // Group paginated data by salesperson (for display)
  const groupedBySp = {};
  paginatedData.forEach(row => {
    const spId = row.salesperson_id || 0;
    if (!groupedBySp[spId]) {
      groupedBySp[spId] = [];
    }
    groupedBySp[spId].push(row);
  });

  // Get salespersons in order
  const activeSalespersons = salespersons.filter(sp => allGroupedBySp[sp.id]);

  const toggleSalesperson = (spId) => {
    const newExpanded = new Set(expandedSp);
    if (newExpanded.has(spId)) {
      newExpanded.delete(spId);
    } else {
      newExpanded.add(spId);
    }
    setExpandedSp(newExpanded);
  };

  const calculateSalespersonTotals = (spId, dataStore = customerData) => {
    const customers = allGroupedBySp[spId] || [];

    // Recalculate totals with current edit values
    // Amount Collected and Balance Collectible are user-inputted manual values
    const totals = customers.reduce((acc, row) => {
      const collected = parseFloat(dataStore[row.customer_id]?.amount_collected !== undefined
        ? dataStore[row.customer_id].amount_collected
        : (row.amount_collected || 0));
      const pdc = parseFloat(dataStore[row.customer_id]?.pdc || row.pdc || 0);
      const discount = parseFloat(dataStore[row.customer_id]?.discount_amount || row.discount_amount || 0);
      const other = parseFloat(dataStore[row.customer_id]?.other_deductions || row.other_deductions || 0);
      // Balance Collectible is manual; use saved value if available, otherwise default to 0
      const balance = dataStore[row.customer_id]?.balance_collectibles !== undefined
        ? parseFloat(dataStore[row.customer_id].balance_collectibles)
        : (parseFloat(row.balance_collectibles) || 0);

      return {
        total_collectibles: acc.total_collectibles + parseFloat(row.total_collectibles || 0),
        posted_collected: acc.posted_collected + parseFloat(row.posted_collected || 0),
        amount_collected: acc.amount_collected + collected,
        pdc: acc.pdc + pdc,
        discount_amount: acc.discount_amount + discount,
        other_deductions: acc.other_deductions + other,
        balance_collectibles: acc.balance_collectibles + balance
      };
    }, {
      total_collectibles: 0,
      posted_collected: 0,
      amount_collected: 0,
      pdc: 0,
      discount_amount: 0,
      other_deductions: 0,
      balance_collectibles: 0
    });

    totals.collection_percentage = totals.total_collectibles > 0
      ? ((totals.amount_collected / totals.total_collectibles) * 100).toFixed(0)
      : 0;

    return totals;
  };

  // Calculate grand totals across ALL data
  const grandTotals = activeSalespersons.reduce((acc, sp) => {
    const spTotals = calculateSalespersonTotals(sp.id, customerData);
    return {
      total_collectibles: acc.total_collectibles + spTotals.total_collectibles,
      posted_collected: acc.posted_collected + spTotals.posted_collected,
      amount_collected: acc.amount_collected + spTotals.amount_collected,
      pdc: acc.pdc + spTotals.pdc,
      discount_amount: acc.discount_amount + spTotals.discount_amount,
      other_deductions: acc.other_deductions + spTotals.other_deductions,
      balance_collectibles: acc.balance_collectibles + spTotals.balance_collectibles
    };
  }, {
    total_collectibles: 0,
    posted_collected: 0,
    amount_collected: 0,
    pdc: 0,
    discount_amount: 0,
    other_deductions: 0,
    balance_collectibles: 0
  });
  grandTotals.collection_percentage = grandTotals.total_collectibles > 0
    ? ((grandTotals.amount_collected / grandTotals.total_collectibles) * 100).toFixed(0)
    : 0;

  const getCurrencyValue = (row, field) => {
    const val = customerData[row.customer_id]?.[field];
    return val !== undefined ? val : (row[field] || 0);
  };

  // Modal functions
  const openEditModal = (row) => {
    const rowData = customerData[row.customer_id] || {};
    const total = parseFloat(row.total_collectibles) || 0;
    // Manual workflow: Amount Collected defaults to 0 and is read-only
    const amount_collected = parseFloat(row.amount_collected) || 0;
    const pdc = parseFloat(getCurrencyValue(row, 'pdc')) || 0;
    const discount_amount = parseFloat(getCurrencyValue(row, 'discount_amount')) || 0;
    const other_deductions = parseFloat(getCurrencyValue(row, 'other_deductions')) || 0;
    // Balance Collectible is manual; use saved value if available, otherwise default to 0
    const balance_collectible = rowData.balance_collectibles !== undefined
      ? parseFloat(rowData.balance_collectibles)
      : (parseFloat(row.balance_collectibles) || 0);

    setEditModal({
      open: true,
      customerId: row.customer_id,
      customerName: row.customer_name,
      total_collectibles: total,
      amount_collected,
      pdc,
      on_dated_cheque: parseFloat(getCurrencyValue(row, 'on_dated_cheque')) || 0,
      discount_amount,
      other_deductions,
      balance_collectible,
      remarks: customerData[row.customer_id]?.remarks || row.remarks || '',
      notes: customerData[row.customer_id]?.notes || row.notes || ''
    });
  };

  const closeEditModal = () => {
    setEditModal(prev => ({ ...prev, open: false }));
  };

  const handleModalChange = (field, value) => {
    setEditModal(prev => {
      const updated = { ...prev, [field]: value };
      // Balance Collectible is manual; only update it when user directly edits it.
      // Amount Collected is read-only and defaults to 0, so it does not trigger recalculation.
      return updated;
    });
  };

  const saveCollectionEntry = async () => {
    setSaving(true);
    try {
      // Save to reports_branch_entries (isolated from AR Aging Details)
      await api.put('/reports/branch-entry', {
        customer_id: editModal.customerId,
        balance_collectibles: parseFloat(editModal.balance_collectible) || 0,
        amount_collected: parseFloat(editModal.amount_collected) || 0,
        pdc: parseFloat(editModal.pdc) || 0,
        on_dated_cheque: parseFloat(editModal.on_dated_cheque) || 0,
        discount_amount: parseFloat(editModal.discount_amount) || 0,
        other_deductions: parseFloat(editModal.other_deductions) || 0,
        remarks: editModal.remarks || '',
        notes: editModal.notes || ''
      });

      // Update local state with saved values
      setCustomerData(prev => ({
        ...prev,
        [editModal.customerId]: {
          ...prev[editModal.customerId],
          balance_collectibles: parseFloat(editModal.balance_collectible) || 0,
          amount_collected: parseFloat(editModal.amount_collected) || 0,
          pdc: parseFloat(editModal.pdc) || 0,
          on_dated_cheque: parseFloat(editModal.on_dated_cheque) || 0,
          discount_amount: parseFloat(editModal.discount_amount) || 0,
          other_deductions: parseFloat(editModal.other_deductions) || 0,
          remarks: editModal.remarks || '',
          notes: editModal.notes || ''
        }
      }));

      // Show toast notification
      showToast('✓ Collection entry saved successfully!', 'success', 4000);
      
      closeEditModal();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save collection entry.';
      console.error('Save collection entry error:', err);
      showToast(`✗ ${errorMessage}`, 'error', 8000);
      setMessage(`Save failed: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Salesperson', 'Customer No.', 'Customer Code', 'Customer Name',
      'Total Collectibles', 'Amount Collected', 'PDC', 'Discount', 'Other Deductions',
      'Balance Collectibles', 'Collection %', 'Remarks', 'Notes'
    ];
    const rows = arData.map((row) => [
      row.salesperson_name || '', row.customer_no || '', row.customer_code || '', row.customer_name || '',
      row.total_collectibles || 0, row.amount_collected || 0, row.pdc || 0, row.discount_amount || 0,
      row.other_deductions || 0, row.balance_collectibles || 0,
      calculateCollectionPercentage(row.total_collectibles, row.amount_collected) + '%',
      row.remarks || '', row.notes || ''
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ar_aging_report.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const calculateCollectionPercentage = (total, collected) => {
    const totalVal = parseFloat(total || 0);
    const collectedVal = parseFloat(collected || 0);
    if (totalVal === 0) return 0;
    return ((collectedVal / totalVal) * 100).toFixed(0);
  };

  return (
    <div className="ar-report-container">
      <div className="page-header">
        <h1>Summary Reports</h1>
        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={refreshCache}
            disabled={syncingCache}
            style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
          >
            {syncingCache ? 'Syncing...' : 'Refresh Cache'}
          </button>
          <button className="btn btn-primary" onClick={exportToCSV}>
            Export to CSV
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-error'}`}>
          {message}
        </div>
      )}

      {/* Cache Status */}
      <div className="cache-status">
        <span>
          <strong>Cache Status:</strong>{' '}
          {cacheStatus.total_records > 0 ? (
            <>
              {cacheStatus.total_records} records cached
              {cacheStatus.last_sync_at && (
                <> · Last synced: {new Date(cacheStatus.last_sync_at).toLocaleString()}</>
              )}
            </>
          ) : (
            'No cached data. Click Refresh Cache to sync.'
          )}
        </span>
      </div>

      {/* Search, Branch & Date Filters */}
      <div className="card report-filters" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        {canSelectAllBranches && (
          <select
            value={selectedBranch}
            onChange={(e) => { setSelectedBranch(e.target.value); setCurrentPage(1); }}
            className="form-control"
            style={{ width: '180px' }}
          >
            <option value={ALL_BRANCHES_VALUE}>All Branches</option>
            {companies.map((company) => (
              <option key={company.id} value={String(company.id)}>
                {company.name}
              </option>
            ))}
          </select>
        )}
        <input
          type="text"
          placeholder="Search by salesperson..."
          value={salespersonFilter}
          onChange={(e) => { setSalespersonFilter(e.target.value); setCurrentPage(1); }}
          className="form-control search-input"
          style={{ flex: 1, minWidth: '180px', maxWidth: '200px' }}
        />
        <input
          type="text"
          placeholder="Search by customer..."
          value={customerFilter}
          onChange={(e) => { setCustomerFilter(e.target.value); setCurrentPage(1); }}
          className="form-control search-input"
          style={{ flex: 1, minWidth: '180px', maxWidth: '200px' }}
        />
        <input
          type="date"
          value={selectedDate || ''}
          onChange={(e) => {
            setSelectedDate(e.target.value);
            setCurrentPage(1);
          }}
          className="form-control"
          style={{ width: '160px' }}
        />
        <button
          onClick={() => {
            setSalespersonFilter('');
            setCustomerFilter('');
            setSelectedDate(new Date().toISOString().split('T')[0]);
            setCurrentPage(1);
          }}
          className="btn btn-sm btn-secondary"
        >
          Clear
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading AR report...</div>
      ) : (
        <>
          {/* AR Report Table - Enhanced with inline editing */}
          <div className="card ar-table-card">
            <div className="table-scroll-hint">
              {ScrollHintIcon}
              <span>Swipe or scroll horizontally to see all columns</span>
            </div>
            <table className="table ar-table table-compact">
              <thead>
                <tr>
                  <th className="sp-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Salesperson</th>
                  <th className="customer-code-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Customer Code</th>
                  <th className="customer-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Customer</th>
                  <th className="number-col total-collectibles-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Total Collectibles</th>
                  <th className="number-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Posted Collected (ERP)</th>
                  <th className="number-col number-editable" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Amount Collected</th>
                  <th className="number-col number-editable" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>PDC</th>
                  <th className="number-col balance" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Bal. Collectibles</th>
                  <th className="percentage-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>%</th>
                  <th className="remarks-col remarks-editable" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Remarks</th>
                  <th className="notes-col notes-editable" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Notes</th>
                  <th className="number-col number-editable" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Discount</th>
                  <th className="number-col number-editable" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Other Deductions</th>
                  <th className="remarks-col remarks-editable" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Updated By</th>
                  <th className="actions-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {salespersons.length === 0 ? (
                  <tr>
                    <td colSpan="14" className="empty-state">No AR data available.</td>
                  </tr>
                ) : (
                  activeSalespersons.map((sp) => {
                    const spCustomers = groupedBySp[sp.id] || [];
                    const spTotals = calculateSalespersonTotals(sp.id, customerData);
                    const isExpanded = expandedSp.has(sp.id);

                    return (
                      <React.Fragment key={sp.id}>
                        {/* Salesperson Header / Subtotal Row */}
                        <tr
                          className="salesperson-row"
                          onClick={() => toggleSalesperson(sp.id)}
                        >
                          <td className="sp-col">
                            <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                            <strong>{sp.name}</strong>
                          </td>
                          <td className="customer-code-col"></td>
                          <td className="customer-col"><strong>Salesperson Total</strong></td>
                          <td className="number-col total-collectibles-col"><strong>{formatCurrency(spTotals.total_collectibles)}</strong></td>
                          <td className="number-col"><strong>{formatCurrency(spTotals.posted_collected)}</strong></td>
                          <td className="number-col"><strong>{formatCurrency(spTotals.amount_collected)}</strong></td>
                          <td className="number-col"><strong>{formatCurrency(spTotals.pdc)}</strong></td>
                          <td className="number-col"><strong>{formatCurrency(spTotals.balance_collectibles)}</strong></td>
                          <td className="percentage-col"><strong>{spTotals.collection_percentage}%</strong></td>
                          <td className="remarks-col"></td>
                          <td className="notes-col"></td>
                          <td className="number-col"><strong>{formatCurrency(spTotals.discount_amount)}</strong></td>
                          <td className="number-col"><strong>{formatCurrency(spTotals.other_deductions)}</strong></td>
                          <td className="remarks-col"></td>
                          <td className="actions-col"></td>
                        </tr>

                        {/* Customer Rows - Display Only with Edit Button */}
                        {isExpanded && spCustomers.map((row, custIndex) => {
                          const rowData = customerData[row.customer_id] || {};
                          const total = parseFloat(row.total_collectibles || 0);
                          // Amount Collected is manual; use saved value if available, otherwise default to 0
                          const collected = rowData.amount_collected !== undefined
                            ? parseFloat(rowData.amount_collected)
                            : (parseFloat(row.amount_collected) || 0);
                          const pdc = parseFloat(getCurrencyValue(row, 'pdc'));
                          const discount = parseFloat(getCurrencyValue(row, 'discount_amount'));
                          const other = parseFloat(getCurrencyValue(row, 'other_deductions'));
                          // Balance Collectible is manual; use saved value if available, otherwise default to 0
                          const balance = rowData.balance_collectibles !== undefined
                            ? parseFloat(rowData.balance_collectibles)
                            : (parseFloat(row.balance_collectibles) || 0);
                          const percentage = total > 0 ? ((collected / total) * 100).toFixed(0) : 0;
                          const currentRemarks = rowData.remarks || row.remarks || '';
                          const currentNotes = rowData.notes || row.notes || '';

                          return (
                            <tr key={row.customer_id} className="customer-row">
                              <td className="sp-col">{row.salesperson_name}</td>
                              <td className="customer-code-col">{row.customer_code || '-'}</td>
                              <td className="customer-col">{row.customer_name}</td>
                              <td className="number-col total-collectibles-col">{formatCurrency(row.total_collectibles)}</td>
                              <td className="number-col">{formatCurrency(row.posted_collected || 0)}</td>
                              <td className="number-col">{formatCurrency(collected)}</td>
                              <td className="number-col">{formatCurrency(pdc)}</td>
                              <td className="number-col balance">
                                {formatCurrency(balance)}
                              </td>
                              <td className="percentage-col">
                                <span className={`percentage-value ${percentage >= 100 ? 'success' : percentage > 0 ? 'warning' : 'danger'}`}>
                                  {percentage}%
                                </span>
                              </td>
                              <td className="remarks-col">{currentRemarks || '-'}</td>
                              <td className="notes-col">{currentNotes || '-'}</td>
                              <td className="number-col">{formatCurrency(discount)}</td>
                              <td className="number-col">{formatCurrency(other)}</td>
                              <td className="remarks-col">{row.updated_by_full_name || '-'}</td>
                              <td className="actions-col">
                                {canEdit ? (
                                  <button
                                    onClick={() => openEditModal(row)}
                                    className="btn btn-primary edit-btn-red"
                                    title="Edit collection entry"
                                  >
                                    ✏️ Edit
                                  </button>
                                ) : (
                                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>Read-only</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })
                )}
                {/* Grand Total Row */}
                {activeSalespersons.length > 0 && (
                  <tr className="grand-total-row" style={{ backgroundColor: '#fef2f2', borderTop: '2px solid #e63740' }}>
                    <td className="sp-col" colSpan="3"><strong>GRAND TOTAL</strong></td>
                    <td className="number-col total-collectibles-col"><strong>{formatCurrency(grandTotals.total_collectibles)}</strong></td>
                    <td className="number-col"><strong>{formatCurrency(grandTotals.posted_collected)}</strong></td>
                    <td className="number-col"><strong>{formatCurrency(grandTotals.amount_collected)}</strong></td>
                    <td className="number-col"><strong>{formatCurrency(grandTotals.pdc)}</strong></td>
                    <td className="number-col"><strong>{formatCurrency(grandTotals.balance_collectibles)}</strong></td>
                    <td className="percentage-col"><strong>{grandTotals.collection_percentage}%</strong></td>
                    <td className="remarks-col"></td>
                    <td className="notes-col"></td>
                    <td className="number-col"><strong>{formatCurrency(grandTotals.discount_amount)}</strong></td>
                    <td className="number-col"><strong>{formatCurrency(grandTotals.other_deductions)}</strong></td>
                    <td className="remarks-col"></td>
                    <td className="actions-col"></td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-sm btn-primary"
                  style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
                >
                  Previous
                </button>

                <span className="pagination-info">
                  Page {currentPage} of {totalPages} ({filteredData.length} records)
                </span>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="btn btn-sm btn-primary"
                  style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Edit Modal */}
      {editModal.open && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Collection Entry</h3>
              <button className="close-btn" onClick={closeEditModal}>✕ Close</button>
            </div>

            <div className="modal-body">
              <div className="customer-info" style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #d0e3f9' }}>
                <div style={{ fontSize: '13px', color: '#5a6c7d', marginBottom: '6px', fontWeight: '500' }}>Customer Name</div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#1a202c' }}>{editModal.customerName}</div>
              </div>

              <div className="form-section-title" style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '12px' }}>Collection Summary</div>
              
              <div className="form-row" style={{ marginBottom: '20px', gap: '20px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Total Collectible</label>
                  <input
                    type="text"
                    value={formatCurrencyWithSeparators(editModal.total_collectibles)}
                    className="form-control"
                    readOnly
                    style={{ backgroundColor: '#f7fafc', fontWeight: '600', color: '#2d3748', fontSize: '15px' }}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Amount Collected</label>
                  <FormattedNumberInput
                    value={editModal.amount_collected}
                    onChange={(val) => handleModalChange('amount_collected', val)}
                    className="form-control"
                    style={{ fontSize: '15px', fontWeight: '600' }}
                  />
                </div>
              </div>

              <div className="form-section-title" style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', marginTop: '24px', marginBottom: '12px' }}>Deductions & Balances</div>
              
              <div className="form-row" style={{ marginBottom: '20px', gap: '20px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>PDC (Post-Dated Checks)</label>
                  <FormattedNumberInput
                    value={editModal.pdc}
                    onChange={(val) => handleModalChange('pdc', val)}
                    className="form-control"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>On Date Cheque</label>
                  <FormattedNumberInput
                    value={editModal.on_dated_cheque}
                    onChange={(val) => handleModalChange('on_dated_cheque', val)}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row" style={{ marginBottom: '20px', gap: '20px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Discount Amount</label>
                  <FormattedNumberInput
                    value={editModal.discount_amount}
                    onChange={(val) => handleModalChange('discount_amount', val)}
                    className="form-control"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Other Deductions</label>
                  <FormattedNumberInput
                    value={editModal.other_deductions}
                    onChange={(val) => handleModalChange('other_deductions', val)}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row" style={{ marginBottom: '24px', gap: '20px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Balance Collectible</label>
                  <FormattedNumberInput
                    value={editModal.balance_collectible}
                    onChange={(val) => handleModalChange('balance_collectible', val)}
                    className="form-control"
                    style={{ fontSize: '15px', backgroundColor: '#fffaf0', fontWeight: '700', color: '#c05621', border: '2px solid #fbd38d' }}
                  />
                </div>
              </div>

              <div className="form-section-title" style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', marginTop: '28px', marginBottom: '12px' }}>Additional Information</div>
              
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Remarks</label>
                <input
                  type="text"
                  value={editModal.remarks}
                  onChange={(e) => handleModalChange('remarks', e.target.value)}
                  className="form-control"
                  placeholder="Enter remarks (optional)"
                  style={{ fontSize: '14px', height: '40px' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#4a5568', marginBottom: '6px' }}>Notes</label>
                <textarea
                  value={editModal.notes}
                  onChange={(e) => handleModalChange('notes', e.target.value)}
                  className="form-control"
                  placeholder="Enter notes (optional)"
                  rows="4"
                  style={{ fontSize: '14px', paddingTop: '10px', paddingBottom: '10px' }}
                />
              </div>
            </div>

            <div className="modal-actions" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', gap: '12px' }}>
              <button
                onClick={closeEditModal}
                className="btn btn-secondary"
                style={{ flex: 1, height: '42px', fontSize: '15px', borderColor: '#e63740', color: '#e63740' }}
              >
                Cancel
              </button>
              <button
                onClick={saveCollectionEntry}
                className="btn btn-primary"
                disabled={saving}
                style={{ flex: 1, height: '42px', fontSize: '15px', fontWeight: '600', backgroundColor: '#e63740', borderColor: '#e63740' }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper component for number inputs with automatic thousand separators
function FormattedNumberInput({ value, onChange, className }) {
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    // Format with thousand separators when value changes
    if (value === '' || value === null || value === undefined) {
      setDisplayValue('');
    } else {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        setDisplayValue(num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
      } else {
        setDisplayValue('');
      }
    }
  }, [value]);

  const handleChange = (e) => {
    const rawValue = e.target.value;
    
    // Remove non-numeric characters except decimal point
    const numericValue = rawValue.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    if ((numericValue.match(/\./g) || []).length > 1) return;
    
    // Set raw value without separators
    setDisplayValue(rawValue);
    
    // Parse and update parent with clean number
    const numValue = parseFloat(numericValue) || 0;
    onChange(numValue);
  };

  return (
    <input
      type="text"
      value={displayValue}
      onChange={handleChange}
      className={className}
      placeholder="0.00"
      style={{ fontSize: '15px' }}
    />
  );
}

// Format currency for read-only display (tables, headers) with Philippine locale
function formatCurrency(value) {
  if (value === undefined || value === null) return '0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Format number with thousand separators but no currency symbol (for inputs)
function formatCurrencyWithSeparators(value) {
  if (value === undefined || value === null) return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default ReportView;
